import { useState } from "react";
import { useFgp } from "@/context/FgpContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AvatarUpload } from "@/components/EntityDialogs";
import { COLOR_OPTIONS, COLOR_KEYS, colorMeta } from "@/lib/colors";
import { User, Building2, Bell, Shield, Palette, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { currentUser, workspace, updateProfile, updateWorkspace, taskCategories, addCategory, updateCategory, deleteCategory, changePassword } = useFgp();
  const [p, setP] = useState({ name: currentUser.name, email: currentUser.email, role: currentUser.role, phone: currentUser.phone || "", timezone: currentUser.timezone || "GMT (London)", avatar: currentUser.avatar || "" });
  const [ws, setWs] = useState({ name: workspace.name, region: workspace.region });
  const [newCat, setNewCat] = useState({ name: "", color: "sky" });
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwLoading, setPwLoading] = useState(false);
  const saveProfile = () => { updateProfile(p); toast.success("Profile saved"); };
  const saveWorkspace = () => { updateWorkspace(ws); toast.success("Workspace updated"); };
  const createCategory = () => {
    if (!newCat.name.trim()) return toast.error("Category name is required");
    addCategory(newCat); toast.success(`"${newCat.name}" category added`); setNewCat({ name: "", color: "sky" });
  };
  const savePassword = async () => {
    if (pw.next.length < 6) return toast.error("New password must be at least 6 characters");
    if (pw.next !== pw.confirm) return toast.error("New passwords do not match");
    setPwLoading(true);
    try {
      await changePassword(pw.current, pw.next);
      toast.success("Password updated");
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err.message || "Could not update password");
    } finally {
      setPwLoading(false);
    }
  };

  const Row = ({ title, desc, defaultOn = true, testid }) => (
    <div className="flex items-center justify-between py-4 border-b border-slate-100 last:border-0">
      <div><p className="text-sm font-medium text-slate-800">{title}</p><p className="text-xs text-slate-400">{desc}</p></div>
      <Switch defaultChecked={defaultOn} data-testid={testid} />
    </div>
  );

  return (
    <div>
      <PageHeader title="Settings" subtitle="Manage your profile, workspace and preferences." />

      <Tabs defaultValue="profile">
        <TabsList className="bg-white border border-slate-200 p-1 mb-6">
          <TabsTrigger value="profile" className="gap-1.5 data-[state=active]:bg-neutral-600 data-[state=active]:text-white"><User className="h-4 w-4" /> Profile</TabsTrigger>
          <TabsTrigger value="workspace" className="gap-1.5 data-[state=active]:bg-neutral-600 data-[state=active]:text-white"><Building2 className="h-4 w-4" /> Workspace</TabsTrigger>
          <TabsTrigger value="notifications" className="gap-1.5 data-[state=active]:bg-neutral-600 data-[state=active]:text-white"><Bell className="h-4 w-4" /> Notifications</TabsTrigger>
          <TabsTrigger value="customization" className="gap-1.5 data-[state=active]:bg-neutral-600 data-[state=active]:text-white"><Palette className="h-4 w-4" /> Customization</TabsTrigger>
          <TabsTrigger value="security" className="gap-1.5 data-[state=active]:bg-neutral-600 data-[state=active]:text-white"><Shield className="h-4 w-4" /> Security</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 max-w-2xl">
            <div className="mb-6"><AvatarUpload value={p.avatar} onChange={(v) => setP({ ...p, avatar: v })} fallback={p.name?.[0]} /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-slate-700">Full name</Label><Input value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} className="mt-1.5" data-testid="settings-name" /></div>
              <div><Label className="text-slate-700">Email</Label><Input value={p.email} onChange={(e) => setP({ ...p, email: e.target.value })} className="mt-1.5" /></div>
              <div><Label className="text-slate-700">Role</Label><Input value={p.role} onChange={(e) => setP({ ...p, role: e.target.value })} className="mt-1.5" /></div>
              <div><Label className="text-slate-700">Phone</Label><Input value={p.phone} onChange={(e) => setP({ ...p, phone: e.target.value })} className="mt-1.5" /></div>
              <div><Label className="text-slate-700">Timezone</Label><Input value={p.timezone} onChange={(e) => setP({ ...p, timezone: e.target.value })} className="mt-1.5" /></div>
            </div>
            <Button onClick={saveProfile} className="mt-6 bg-neutral-600 hover:bg-neutral-700" data-testid="settings-save">Save changes</Button>
          </div>
        </TabsContent>

        <TabsContent value="workspace">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 max-w-2xl">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-slate-700">Workspace name</Label><Input value={ws.name} onChange={(e) => setWs({ ...ws, name: e.target.value })} className="mt-1.5" data-testid="settings-ws-name" /></div>
              <div><Label className="text-slate-700">Plan</Label><Input value={workspace.plan} disabled className="mt-1.5" /></div>
              <div><Label className="text-slate-700">Region</Label><Input value={ws.region} onChange={(e) => setWs({ ...ws, region: e.target.value })} className="mt-1.5" /></div>
              <div><Label className="text-slate-700">Members</Label><Input value={workspace.members} disabled className="mt-1.5" /></div>
            </div>
            <Button onClick={saveWorkspace} className="mt-6 bg-neutral-600 hover:bg-neutral-700" data-testid="settings-ws-save">Save workspace</Button>
            <div className="mt-6 rounded-lg bg-slate-50 border border-slate-200 p-4 flex items-center gap-3">
              <Shield className="h-5 w-5 text-neutral-600" />
              <p className="text-sm text-slate-600">Your workspace is on the <strong>{workspace.plan}</strong> plan since {new Date(workspace.createdAt).toLocaleDateString("en-GB", { month: "long", year: "numeric" })}.</p>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="notifications">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 max-w-2xl">
            <Row title="Document expiry alerts" desc="Notify me 60 days before any document expires" testid="notif-docs" />
            <Row title="Renewal reminders" desc="Reminders for upcoming insurance, visa and fee renewals" testid="notif-renewals" />
            <Row title="Daily AI brief" desc="Receive the AI daily brief each morning" testid="notif-brief" />
            <Row title="Task assignments" desc="When a task is assigned to me" testid="notif-tasks" />
            <Row title="Weekly summary email" desc="A digest of household activity every Monday" defaultOn={false} testid="notif-weekly" />
          </div>
        </TabsContent>

        <TabsContent value="customization">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 max-w-2xl" data-testid="customization-panel">
            <div className="mb-5">
              <h3 className="font-heading font-semibold text-slate-900">Task categories</h3>
              <p className="text-sm text-slate-400">Colour-code your tasks the way you work. Changes apply everywhere instantly.</p>
            </div>

            <div className="space-y-2.5 mb-6">
              {taskCategories.map((c) => (
                <div key={c.id} className="flex items-center gap-3 rounded-lg border border-slate-200 p-3" data-testid={`category-row-${c.id}`}>
                  <span className={`h-4 w-4 rounded-full flex-shrink-0 ${colorMeta(c.color).dot}`} />
                  <Input value={c.name} onChange={(e) => updateCategory(c.id, { name: e.target.value })} className="flex-1 h-9" data-testid={`category-name-${c.id}`} />
                  <div className="flex items-center gap-1">
                    {COLOR_KEYS.map((k) => (
                      <button key={k} onClick={() => updateCategory(c.id, { color: k })} title={COLOR_OPTIONS[k].label}
                        className={`h-5 w-5 rounded-full ${COLOR_OPTIONS[k].solid} transition-transform hover:scale-110 ${c.color === k ? "ring-2 ring-offset-1 ring-slate-800" : ""}`}
                        data-testid={`category-color-${c.id}-${k}`} />
                    ))}
                  </div>
                  <button onClick={() => { deleteCategory(c.id); toast.success("Category removed"); }} disabled={c.id === "cat_general"}
                    className="text-slate-400 hover:text-rose-600 disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0" data-testid={`category-delete-${c.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>

            <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
              <p className="text-sm font-medium text-slate-700 mb-2.5">Add a category</p>
              <div className="flex items-center gap-3">
                <Input value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} placeholder="e.g. Finance" className="flex-1 h-9 bg-white" data-testid="new-category-name" />
                <div className="flex items-center gap-1">
                  {COLOR_KEYS.map((k) => (
                    <button key={k} onClick={() => setNewCat({ ...newCat, color: k })} title={COLOR_OPTIONS[k].label}
                      className={`h-5 w-5 rounded-full ${COLOR_OPTIONS[k].solid} transition-transform hover:scale-110 ${newCat.color === k ? "ring-2 ring-offset-1 ring-slate-800" : ""}`}
                      data-testid={`new-category-color-${k}`} />
                  ))}
                </div>
                <Button onClick={createCategory} className="bg-neutral-600 hover:bg-neutral-700 gap-1.5 h-9" data-testid="add-category-btn"><Plus className="h-4 w-4" /> Add</Button>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="security">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 max-w-2xl" data-testid="security-panel">
            <div className="mb-5">
              <h3 className="font-heading font-semibold text-slate-900">Change password</h3>
              <p className="text-sm text-slate-400">
                {currentUser?.authProvider === "google"
                  ? "You signed in with Google. Set a password here to also enable email + password login."
                  : "Update your password. You'll need your current password to confirm."}
              </p>
            </div>
            <div className="space-y-4 max-w-sm">
              {currentUser?.authProvider !== "google" && (
                <div><Label className="text-slate-700">Current password</Label><Input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} className="mt-1.5" data-testid="pw-current" placeholder="••••••••" /></div>
              )}
              <div><Label className="text-slate-700">New password</Label><Input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} className="mt-1.5" data-testid="pw-new" placeholder="At least 6 characters" /></div>
              <div><Label className="text-slate-700">Confirm new password</Label><Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="mt-1.5" data-testid="pw-confirm" placeholder="Re-enter new password" /></div>
              <Button onClick={savePassword} disabled={pwLoading} className="bg-neutral-600 hover:bg-neutral-700" data-testid="pw-save">
                {pwLoading ? "Saving…" : "Update password"}
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
